/** Sparse-set component store: O(1) add/remove/lookup with a packed dense array
 * for cache-friendly iteration. One store exists per component type. */
import type { EntityId } from '@settlers/shared';

export class ComponentStore<T> {
  /** Packed component data, parallel to `denseEntities`. */
  private readonly data: T[] = [];
  /** Packed entity ids owning each `data` slot. */
  private readonly denseEntities: EntityId[] = [];
  /** entity id -> index into the packed arrays. */
  private readonly sparse = new Map<EntityId, number>();

  has(entity: EntityId): boolean {
    return this.sparse.has(entity);
  }

  get(entity: EntityId): T | undefined {
    const idx = this.sparse.get(entity);
    return idx === undefined ? undefined : this.data[idx];
  }

  set(entity: EntityId, value: T): void {
    const existing = this.sparse.get(entity);
    if (existing !== undefined) {
      this.data[existing] = value;
      return;
    }
    this.sparse.set(entity, this.data.length);
    this.data.push(value);
    this.denseEntities.push(entity);
  }

  /** Swap-remove. Returns true if the entity had this component. */
  remove(entity: EntityId): boolean {
    const idx = this.sparse.get(entity);
    if (idx === undefined) return false;

    const lastIdx = this.data.length - 1;
    const lastEntity = this.denseEntities[lastIdx] as EntityId;
    const lastData = this.data[lastIdx] as T;

    this.data[idx] = lastData;
    this.denseEntities[idx] = lastEntity;
    this.sparse.set(lastEntity, idx);

    this.data.pop();
    this.denseEntities.pop();
    this.sparse.delete(entity);
    return true;
  }

  get size(): number {
    return this.data.length;
  }

  /** Entities currently holding this component (packed order — not sorted). */
  entities(): readonly EntityId[] {
    return this.denseEntities;
  }
}
